import { useEffect, useState, type CSSProperties } from 'react';
import { useGameStore } from '../store/gameStore';
import {
  HqItem,
  PERMANENT_RESIDENTS,
  cheapestUnowned,
  itemById,
  itemsInZone,
  zonesInOrder,
} from '../content/hq';
import { Sprite } from './Sprite';
import { playEarcon } from '../audio/player';
import { useCountUp } from './useCountUp';

const HERO_NAMES: Record<string, string> = {
  spidey: 'Spider-Man',
  'iron-man': 'Iron Man',
  thor: 'Thor',
};
const heroName = (id: string) => HERO_NAMES[id] ?? id;

/** Renders an HQ item's art: the generated pixel-art PNG (public/hq/<id>.png), falling
 *  back to its emoji if the image is missing. `dim` greys an un-built slot. */
function HqArt({ item, size, dim }: { item: HqItem; size: number; dim?: boolean }) {
  const [failed, setFailed] = useState(false);
  const style: CSSProperties = dim ? { opacity: 0.32, filter: 'grayscale(1)' } : {};
  if (failed) return <span style={{ fontSize: size * 0.82, lineHeight: 1, ...style }}>{item.art}</span>;
  return (
    <img
      src={`${import.meta.env.BASE_URL}hq/${item.id}.png`}
      width={size}
      height={size}
      alt=""
      draggable={false}
      onError={() => setFailed(true)}
      style={{ imageRendering: 'pixelated', ...style }}
    />
  );
}

/** The reward economy (Phase 4): spend Hero Coins — minted only by decoding — to build &
 *  decorate the Avengers Base. A cutaway tower that grows over weeks; a featured goal
 *  pulls him toward the next build. No loot boxes, no shortcuts past reading. */
export function BaseView({ onClose }: { onClose: () => void }) {
  const coins = useGameStore((s) => s.coins);
  const owned = useGameStore((s) => s.owned);
  const featuredGoal = useGameStore((s) => s.featuredGoal);
  const purchase = useGameStore((s) => s.purchase);
  const setGoal = useGameStore((s) => s.setGoal);

  const [celebrating, setCelebrating] = useState<HqItem | null>(null);
  const shownCoins = useCountUp(coins);

  const goalId = featuredGoal ?? cheapestUnowned(owned)?.id ?? null;
  const goal = goalId ? itemById(goalId) ?? null : null;

  const build = (item: HqItem) => {
    if (owned.includes(item.id) || coins < item.cost) return;
    purchase(item.id);
    playEarcon('build');
    setCelebrating(item);
  };

  return (
    <div style={overlay}>
      <style>{KEYFRAMES}</style>

      <div style={header}>
        <span style={title}>🕸️ Avengers Base</span>
        <span style={coinChip}>🪙 {shownCoins}</span>
        <button type="button" style={closeBtn} onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      {goal ? (
        <GoalBanner goal={goal} coins={coins} onBuild={() => build(goal)} />
      ) : (
        <div style={allDone}>🎉 Your base is complete! Amazing work, hero.</div>
      )}

      <div style={tower}>
        {zonesInOrder().map((zone) => {
          const items = itemsInZone(zone.id);
          const residents = [
            ...PERMANENT_RESIDENTS.filter((r) => r.zone === zone.id).map((r) => r.heroId),
            ...items.filter((i) => owned.includes(i.id) && i.residentHero).map((i) => i.residentHero as string),
          ];
          return (
            <div key={zone.id} style={floor}>
              <span style={floorLabel}>{zone.name}</span>
              <div style={slots}>
                {items.map((item) => {
                  const built = owned.includes(item.id);
                  const isGoal = item.id === goalId;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      disabled={built}
                      onClick={() => !built && setGoal(item.id)}
                      style={{ ...slot, ...(built ? slotBuilt : slotEmpty), ...(isGoal && !built ? slotGoal : null) }}
                      aria-label={built ? `${item.name} (built)` : `${item.name}, ${item.cost} coins`}
                    >
                      <HqArt item={item} size={44} dim={!built} />
                      <span style={slotName}>{item.name}</span>
                      {!built && <span style={slotCost}>🪙 {item.cost}</span>}
                    </button>
                  );
                })}
                {residents.map((heroId, i) => (
                  <span key={`${heroId}-${i}`} style={resident} title={heroName(heroId)}>
                    <Sprite heroId={heroId} kind="idle" size={56} fps={5} />
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {celebrating && <Celebration item={celebrating} onDone={() => setCelebrating(null)} />}
    </div>
  );
}

function GoalBanner({ goal, coins, onBuild }: { goal: HqItem; coins: number; onBuild: () => void }) {
  const affordable = coins >= goal.cost;
  const pct = Math.min(100, Math.round((coins / goal.cost) * 100));
  return (
    <div style={banner}>
      <HqArt item={goal} size={52} />
      <div style={bannerMid}>
        <div style={bannerLabel}>★ NEXT BUILD</div>
        <div style={bannerName}>{goal.name}</div>
        <div style={barTrack}>
          <div style={{ ...barFill, width: `${pct}%` }} />
        </div>
        <div style={barText}>
          {Math.min(coins, goal.cost)} / {goal.cost} 🪙
          {!affordable && <span style={toGo}> · {goal.cost - coins} to go</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={onBuild}
        disabled={!affordable}
        style={{ ...buildBtn, ...(affordable ? buildBtnReady : buildBtnWait) }}
      >
        {affordable ? 'BUILD! 🔨' : 'Keep reading'}
      </button>
    </div>
  );
}

function Celebration({ item, onDone }: { item: HqItem; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2200);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={celebOverlay} onClick={onDone}>
      {CONFETTI.map((c, i) => (
        <span key={i} style={{ ...confetti, left: c.left, animationDelay: c.delay }}>
          {c.emoji}
        </span>
      ))}
      <div style={celebCard}>
        <HqArt item={item} size={104} />
        <div style={celebTitle}>Built!</div>
        <div style={celebName}>{item.name}</div>
        {item.residentHero && (
          <div style={celebResident}>{heroName(item.residentHero)} joined your base!</div>
        )}
        <div style={{ marginTop: 6 }}>
          <Sprite heroId="spidey" kind="celebrate" size={120} fps={8} loop={false} />
        </div>
      </div>
    </div>
  );
}

const CONFETTI = [
  { emoji: '🎉', left: '8%', delay: '0s' },
  { emoji: '✨', left: '20%', delay: '0.15s' },
  { emoji: '⭐', left: '33%', delay: '0.05s' },
  { emoji: '🎊', left: '47%', delay: '0.2s' },
  { emoji: '✨', left: '60%', delay: '0.1s' },
  { emoji: '⭐', left: '73%', delay: '0.25s' },
  { emoji: '🎉', left: '86%', delay: '0.08s' },
  { emoji: '✨', left: '93%', delay: '0.18s' },
];

const KEYFRAMES = `
@keyframes ctc-pop { 0% { transform: scale(0.3); opacity: 0; } 60% { transform: scale(1.18); } 100% { transform: scale(1); opacity: 1; } }
@keyframes ctc-confetti { 0% { transform: translateY(-30px) rotate(0deg); opacity: 1; } 100% { transform: translateY(70vh) rotate(360deg); opacity: 0; } }
@keyframes ctc-glow { 0%, 100% { box-shadow: 0 0 0 0 rgba(54, 179, 126, 0.6); } 50% { box-shadow: 0 0 0 9px rgba(54, 179, 126, 0); } }
`;

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 40,
  background: 'var(--frame)',
  display: 'flex',
  flexDirection: 'column',
  padding: '14px 18px',
  overflow: 'hidden',
};
const header: CSSProperties = { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 };
const title: CSSProperties = { fontSize: 26, fontWeight: 800, color: 'var(--gold)', flex: 1 };
const coinChip: CSSProperties = {
  padding: '6px 16px',
  borderRadius: 999,
  background: 'var(--frame-2)',
  color: 'var(--gold)',
  fontWeight: 800,
  fontSize: 22,
};
const closeBtn: CSSProperties = {
  width: 'var(--target-min)',
  height: 'var(--target-min)',
  borderRadius: '50%',
  border: 'none',
  background: 'var(--frame-2)',
  color: '#f4f4f4',
  fontSize: 24,
  cursor: 'pointer',
};

const banner: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  background: 'var(--frame-2)',
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: 'var(--gold)',
  borderRadius: 18,
  padding: '12px 18px',
  marginBottom: 12,
};
const bannerMid: CSSProperties = { flex: 1, minWidth: 0 };
const bannerLabel: CSSProperties = { fontSize: 13, fontWeight: 800, color: 'var(--gold)', letterSpacing: 1 };
const bannerName: CSSProperties = { fontSize: 22, fontWeight: 800, color: '#f4f4f4', marginBottom: 6 };
const barTrack: CSSProperties = { height: 14, borderRadius: 999, background: 'var(--frame)', overflow: 'hidden' };
const barFill: CSSProperties = { height: '100%', background: 'var(--gold)', borderRadius: 999, transition: 'width 0.5s ease' };
const barText: CSSProperties = { fontSize: 14, fontWeight: 700, color: '#cdd6ee', marginTop: 4 };
const toGo: CSSProperties = { color: '#8ca0c4' };
const buildBtn: CSSProperties = {
  height: 'var(--target-min)',
  padding: '0 26px',
  borderRadius: 999,
  border: 'none',
  fontSize: 20,
  fontWeight: 800,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const buildBtnReady: CSSProperties = { background: 'var(--good)', color: '#fff', animation: 'ctc-pulse 1.1s ease-in-out infinite' };
const buildBtnWait: CSSProperties = { background: 'var(--frame)', color: '#6b7693', cursor: 'not-allowed' };

const allDone: CSSProperties = {
  background: 'var(--frame-2)',
  borderRadius: 18,
  padding: '16px 18px',
  marginBottom: 12,
  color: 'var(--gold)',
  fontSize: 20,
  fontWeight: 800,
  textAlign: 'center',
};

const tower: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  overflow: 'auto',
  borderRadius: 16,
};
const floor: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  background: 'var(--frame-2)',
  borderTop: '2px solid var(--frame-edge)',
  borderRadius: 12,
  padding: '8px 14px',
  minHeight: 84,
};
const floorLabel: CSSProperties = {
  width: 78,
  flexShrink: 0,
  fontSize: 13,
  fontWeight: 800,
  color: '#8ca0c4',
  textTransform: 'uppercase',
  letterSpacing: 1,
};
const slots: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' };
const slot: CSSProperties = {
  width: 96,
  minHeight: 88,
  borderRadius: 14,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  padding: 6,
  cursor: 'pointer',
  borderWidth: 2,
  borderStyle: 'solid',
};
const slotBuilt: CSSProperties = { background: 'var(--frame)', borderColor: 'var(--good)', cursor: 'default' };
const slotEmpty: CSSProperties = { background: 'var(--frame)', borderColor: 'var(--frame-edge)' };
const slotGoal: CSSProperties = { borderColor: 'var(--gold)' };
const slotName: CSSProperties = { fontSize: 12, fontWeight: 700, color: '#cdd6ee', textAlign: 'center' };
const slotCost: CSSProperties = { fontSize: 13, fontWeight: 800, color: 'var(--gold)' };
const resident: CSSProperties = { display: 'inline-flex', alignItems: 'flex-end', alignSelf: 'flex-end' };

const celebOverlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 60,
  background: 'rgba(8, 11, 24, 0.78)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};
const confetti: CSSProperties = { position: 'absolute', top: 0, fontSize: 34, animation: 'ctc-confetti 1.8s linear forwards' };
const celebCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  animation: 'ctc-pop 0.5s ease-out',
};
const celebTitle: CSSProperties = { fontSize: 34, fontWeight: 800, color: 'var(--gold)' };
const celebName: CSSProperties = { fontSize: 24, fontWeight: 800, color: '#f4f4f4' };
const celebResident: CSSProperties = { fontSize: 18, fontWeight: 700, color: 'var(--good)', marginTop: 2 };
